// Числата над таблицата на „Дневни отчети → Персонални".
//
// Всички суми идват от BE като Decimal-НИЗОВЕ и се сумират едва след
// `parseDecimal` — иначе „12.50" + „7.00" би дало конкатенация.
//
// „Друго" е остатъкът извън в брой и карта („По сметка" и персонализираните
// методи). BE-то го смята като `total − cash − card`, затова е верен и за
// отчетите, записани преди разбивката по методи да съществува — без него
// заведение, което приема плащане по сметка, вижда оборот, който не се
// покрива от двете колони.

import dayjs from "dayjs";

import { EM_DASH, parseDecimal } from "../../../../components/dataTable";
import type { Report } from "../../../../api/reports";

/**
 * Работният ден като „04.09.2026" — ръчен формат, защото
 * `toLocaleDateString("bg-BG")` лепи „ г." и клетката се пренася на два реда.
 */
export const formatWorkDay = (value: string | null | undefined): string => {
  if (!value) return EM_DASH;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD.MM.YYYY") : EM_DASH;
};

export type PersonalReportsSummary = {
  /** Брой пуснати персонални отчети в периода. */
  reports: number;
  bills: number;
  total: number;
  cash: number;
  card: number;
  other: number;
};

export const summarizePersonalReports = (
  reports: readonly Report[],
): PersonalReportsSummary => {
  const summary: PersonalReportsSummary = {
    reports: reports.length,
    bills: 0,
    total: 0,
    cash: 0,
    card: 0,
    other: 0,
  };

  for (const report of reports) {
    // Единственото цяло число в отговора — броячите по метод са Decimal,
    // защото смесеното плащане се разпределя дробно.
    summary.bills += Number(report.total_bills_count ?? 0);
    summary.total += parseDecimal(report.total_amount);
    summary.cash += parseDecimal(report.cash_amount);
    summary.card += parseDecimal(report.card_amount);
    summary.other += parseDecimal(report.other_amount);
  }

  return summary;
};

/** Стойност, която се чете като нула след закръгляне до стотинки. */
export const isZeroAmount = (amount: number): boolean =>
  Math.abs(amount) < 0.005;

/**
 * Делът на един метод от оборота.
 *
 * `null` = делът НЕ СЪЩЕСТВУВА (няма оборот), а не „0 %": период без нито
 * един отчет не бива да твърди, че в брой е 0 % от нищо.
 */
export const shareOf = (part: number, total: number): number | null =>
  total > 0 ? (part / total) * 100 : null;
